// src/pages/Messages.js
import React, { useState, useEffect } from 'react';
import { getPatients, sendWhatsAppMessage } from '../services/api';
import toast from 'react-hot-toast';

export default function Messages() {
  const [patients, setPatients] = useState([]);
  const [selected, setSelected] = useState('');
  const [to, setTo] = useState('');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);

  useEffect(() => {
    getPatients()
      .then((data) => setPatients(Array.isArray(data) ? data : []))
      .catch(() => toast.error('Failed to load patients'));
  }, []);

  const onPick = (e) => {
    const phone = e.target.value;
    setSelected(phone);
    if (phone) setTo(phone.replace(/\D/g, ''));
  };

  const send = async () => {
    const cleanTo = to.replace(/\D/g, '');
    if (cleanTo.length < 10) return toast.error('Enter a valid recipient number');
    if (!message.trim()) return toast.error('Enter a message');
    setSending(true);
    try {
      await sendWhatsAppMessage(cleanTo, message.trim());
      toast.success('Message sent ✅');
      setMessage('');
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const selectedName = patients.find((p) => p.whatsappPhone === selected)?.name;

  return (
    <div style={styles.page}>
      <div style={styles.pageHeader}>
        <h1 style={styles.pageTitle}>Send WhatsApp Message</h1>
      </div>

      <div style={styles.note}>
        ℹ️ Free-text messages only reach patients who messaged the bot in the last <b>24 hours</b>.
        Outside that window WhatsApp requires an approved template, and the send will fail with a notice.
      </div>

      <div style={styles.card}>
        <div style={styles.field}>
          <label style={styles.label}>Select a patient</label>
          <select value={selected} onChange={onPick} style={styles.input}>
            <option value="">— Choose a patient —</option>
            {patients.map((p) => (
              <option key={p.whatsappPhone} value={p.whatsappPhone}>
                {p.name} ({p.mobileNumber || p.whatsappPhone})
              </option>
            ))}
          </select>
        </div>

        <div style={styles.field}>
          <label style={styles.label}>Recipient number (or type one)</label>
          <input
            value={to}
            onChange={(e) => { setTo(e.target.value); setSelected(''); }}
            placeholder="e.g. 919391714623"
            style={styles.input}
          />
        </div>

        <div style={styles.field}>
          <label style={styles.label}>Message{selectedName ? ` to ${selectedName}` : ''}</label>
          <textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            rows={5}
            placeholder="Type your message…"
            style={{ ...styles.input, resize: 'vertical' }}
          />
          <div style={styles.counter}>{message.length} characters</div>
        </div>

        <button onClick={send} disabled={sending} style={{ ...styles.sendBtn, opacity: sending ? 0.6 : 1 }}>
          {sending ? 'Sending…' : '💬 Send WhatsApp Message'}
        </button>
      </div>
    </div>
  );
}

const styles = {
  page: { padding: '28px', maxWidth: '680px' },
  pageHeader: { marginBottom: '20px' },
  pageTitle: { fontSize: '26px', fontWeight: '700', color: '#0f172a', margin: 0 },
  note: {
    background: '#fffbeb', border: '1px solid #fde68a', color: '#92400e',
    borderRadius: '10px', padding: '12px 16px', fontSize: '13px', marginBottom: '20px', lineHeight: 1.5,
  },
  card: { background: 'white', borderRadius: '16px', boxShadow: '0 1px 3px rgba(0,0,0,0.07)', padding: '24px' },
  field: { display: 'flex', flexDirection: 'column', gap: '6px', marginBottom: '18px' },
  label: { fontSize: '12px', fontWeight: '600', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.04em' },
  input: {
    padding: '11px 14px', border: '2px solid #e5e7eb', borderRadius: '10px',
    fontSize: '14px', outline: 'none', fontFamily: 'inherit', width: '100%', boxSizing: 'border-box',
  },
  counter: { fontSize: '11px', color: '#94a3b8', textAlign: 'right' },
  sendBtn: {
    padding: '12px 20px', background: '#0f766e', color: 'white', border: 'none',
    borderRadius: '10px', cursor: 'pointer', fontSize: '15px', fontWeight: '600', width: '100%',
  },
};
